import { usePostHogStore } from "../stores/posthogStore";

/**
 * Attach PostHog distinct_id to all Axios requests as a header.
 *
 * Default header name: "X-PostHog-Distinct-Id".
 * Options:
 *  - headerName?: string
 *  - pinia?: Pinia instance (recommended when using outside components)
 *  - getStore?: () => store instance (advanced override)
 */
export function attachAxiosPostHog(axios, options = {}) {
  // Respect build-time environment flag to disable PostHog completely
  const v = String(process.env.MIX_POSTHOG_DISABLED || "").toLowerCase();
  if (v === "true") {
    console.log("PostHog is disabled via MIX_POSTHOG_DISABLED");
    return; // No interceptor, no store.init
  }

  if (!axios || typeof axios.interceptors?.request?.use !== "function") {
    console.error("Invalid axios instance provided to attachAxiosPostHog");
    return;
  }

  const headerName = options.headerName || "X-PostHog-Distinct-Id";
  let store;

  // Install a request interceptor to set/update the header for every request
  axios.interceptors.request.use((config) => {
    try {
      // Lazily resolve the store when Pinia is available (prevents "No active Pinia" errors)
      if (!store) {
        if (typeof options.getStore === "function") {
          store = options.getStore();
        } else if (options.pinia) {
          try {
            store = usePostHogStore(options.pinia);
          } catch (_) {
            // Fallback: try without explicit pinia if active pinia is already set
            try {
              store = usePostHogStore();
            } catch (_) {
            }
          }
        } else {
          try {
            store = usePostHogStore();
          } catch (_) {
            store = null;
          }
        }
        if (store && !store.isReady && typeof store.init === "function") {
          store.init();
        }
      }

      const did = store?.getDistinctId?.();
      if (did) {
        if (process.env.NODE_ENV === "development") {
          // console.log(`[DEV] Attaching PostHog distinct_id to header ${headerName}: ${did}`);
        }
        if (!config.headers) config.headers = {};
        if (typeof config.headers.set === "function") {
          if (!config.headers.has(headerName)) {
            config.headers.set(headerName, did);
          }
        } else if (!config.headers[headerName]) {
          config.headers[headerName] = did;
        }
      }
    } catch (_) {
    }
    return config;
  });
}
